import React, { useCallback, useSyncExternalStore } from 'react'

/**
 * Developer mode — annotates the UI with what each decision becomes on the Intune side: the Graph
 * calls a publish issues, and the PSADT command line the behaviour rides on. The mock's
 * `DevMode.tsx`, ported.
 *
 * It changes nothing about the configuration; it is a reading aid for whoever has to answer "what
 * did this screen actually do".
 *
 * ONE DIFFERENCE FROM THE MOCK, AND IT MATTERS. The mock's panel is captioned "Indicative — the
 * mock does not call Graph", because nothing there reaches a tenant. Ours does. So every block is
 * generated from the same value the publish path sends — the assign body through
 * `rowToAssignmentCreate`, the command line through the backend's own `preview-commands` — rather
 * than hand-written to look plausible. A panel that agreed with itself and disagreed with
 * production would be worse than having none, because it would be believed.
 *
 * No provider: the store is module-level and read through `useSyncExternalStore`, so the toggle
 * works without wiring anything into App.tsx.
 */

const KEY = 'aeam-devmode'

const listeners = new Set<() => void>()
let on = false
try {
  on = localStorage.getItem(KEY) === '1'
} catch {
  // Private-browsing or a locked-down profile. Dev mode simply stays off.
}

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
const getSnapshot = () => on

export function useDevMode(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

export function useDevModeToggle(): { on: boolean; toggle: () => void } {
  const value = useDevMode()
  const toggle = useCallback(() => {
    on = !on
    try {
      localStorage.setItem(KEY, on ? '1' : '0')
    } catch {
      // Not persisting is survivable; not toggling is not.
    }
    listeners.forEach((fn) => fn())
  }, [])
  return { on: value, toggle }
}

/** The switch. Not mounted on any customer-facing screen: dev mode is turned on by setting
 *  localStorage `aeam-devmode` to `1` and reloading. */
export function DevModeToggle() {
  const { on: isOn, toggle } = useDevModeToggle()
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isOn}
      title="Show the Graph calls and the PSADT command line this deployment produces"
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] transition-colors ${
        isOn
          ? 'border-violet-400/50 bg-violet-400/10 text-violet-300'
          : 'border-theme-border-primary text-theme-text-tertiary hover:text-theme-text-primary'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${isOn ? 'bg-violet-400' : 'bg-theme-text-tertiary'}`}
      />
      Developer mode
    </button>
  )
}

/** Inline annotation shown under a control when dev mode is on. */
export function DevNote({ setting, example }: { setting: string; example?: string }) {
  if (!useDevMode()) return null
  return (
    <div className="mt-1.5 rounded border-l-2 border-violet-400/60 bg-violet-400/[0.07] px-2.5 py-1.5">
      <code className="block font-mono text-[11px] leading-relaxed text-violet-300">{setting}</code>
      {example && (
        <code className="mt-0.5 block font-mono text-[11px] leading-relaxed text-violet-300/60">
          {example}
        </code>
      )}
    </div>
  )
}

/** Block of generated script, shown only in dev mode. */
export function DevScript({ title, lines }: { title: string; lines: string[] }) {
  if (!useDevMode()) return null
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-violet-400/30 bg-violet-400/[0.05]">
      <div className="border-b border-violet-400/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-300/80">
        {title}
      </div>
      <pre className="overflow-x-auto px-3 py-2.5 font-mono text-[11px] leading-relaxed text-violet-100">
        {lines.join('\n')}
      </pre>
    </div>
  )
}
